import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PLACEHOLDER_PHONE, PLACEHOLDER_EMAIL } from '../lib/contactDefaults';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Users,
  Building2,
  User,
  HardHat,
  Phone,
  Mail,
  MapPin,
  FileText,
  Edit,
  Trash2,
  Loader2,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronRight,
  ChevronLeft,
  Eye,
  Briefcase,
  FolderOpen,
  Wrench,
  Calculator,
  Download
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const CATEGORY_OPTIONS = ['Client', 'Architect', 'Contractor', 'Structural Engineer', 'Supplier'];
const CONSENT_OPTIONS = ['Yes', 'No', 'Awaiting'];

// Common supplier trades. "Material Supplier" is pinned at the top per
// SD-1 §4 — it distinguishes suppliers who provide raw materials (timber,
// plaster, brick etc) from trades-based sub-contractors.
const SUPPLIER_TRADES = [
  'Material Supplier',
  'Bricklayer',
  'Carpenter',
  'Electrician',
  'Plumber',
  'Plasterer',
  'Roofer',
  'Groundworker',
  'Scaffolder',
  'Painter & Decorator',
  'Tiler',
  'Kitchen Fitter',
  'Window Fitter',
  'Steelwork',
  'Insulation',
  'Flooring',
  'Other'
];

const CATEGORY_COLORS = {
  'Client': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Architect': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'Contractor': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  'Structural Engineer': 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  'Supplier': 'bg-green-500/20 text-green-400 border-green-500/30'
};

const CONSENT_COLORS = {
  'Yes': 'bg-green-500/20 text-green-400 border-green-500/30',
  'No': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Awaiting': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const CATEGORY_ICONS = {
  'Client': User,
  'Architect': Building2,
  'Contractor': HardHat,
  'Structural Engineer': Calculator,
  'Supplier': Wrench
};

export function ContactsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [consentFilter, setConsentFilter] = useState('All');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [relatedItems, setRelatedItems] = useState(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    surname: '',
    address: '',
    postcode: '',
    telephone: PLACEHOLDER_PHONE,
    email: PLACEHOLDER_EMAIL,
    company: '',
    category: 'Contractor',
    trade: '',
    marketing_consent: 'Awaiting',
    notes: '',
    show_in_diary: false,
  });
  
  const { api, isAdmin, canEditRecords, canDeleteRecords } = useAuth();

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (categoryFilter && categoryFilter !== 'All') params.append('category', categoryFilter);
      if (consentFilter && consentFilter !== 'All') params.append('marketing_consent', consentFilter);
      
      const response = await api.get(`/contacts?${params.toString()}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [api, search, categoryFilter, consentFilter]);

  // useEffect must come AFTER the useCallback above — `const` bindings are
  // in the TDZ until their line executes, and React evaluates the dep array
  // during render top-to-bottom.
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const resetForm = () => {
    setFormData({
      first_name: '',
      surname: '',
      address: '',
      postcode: '',
      telephone: PLACEHOLDER_PHONE,
      email: PLACEHOLDER_EMAIL,
      company: '',
      category: 'Contractor',
      trade: '',
      marketing_consent: 'Awaiting',
      notes: '',
      show_in_diary: false,
    });
  };

  // "+ New → New contact" from the top bar lands here with ?new=1
  useEffect(() => {
    if (searchParams.get('new')) {
      resetForm();
      setCreateDialogOpen(true);
      setSearchParams((p) => { const n = new URLSearchParams(p); n.delete('new'); return n; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleCreate = async () => {
    if (!formData.first_name.trim()) {
      toast.error('Please enter a first name (or company name)');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/contacts', formData);
      toast.success('Contact created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = async (contact) => {
    setSelectedContact(contact);
    setRelatedItems(null);
    setViewDialogOpen(true);
    setLoadingRelated(true);
    
    try {
      const response = await api.get(`/contacts/${contact.id}/related`);
      setRelatedItems(response.data);
    } catch (error) {
      console.error('Failed to fetch related items:', error);
      toast.error('Failed to load related projects/tenders');
    } finally {
      setLoadingRelated(false);
    }
  };

  const handleEdit = (contact) => {
    setSelectedContact(contact);
    setFormData({
      // SD-2 §3 — split fields are the source of truth; fall back to the
      // legacy `name` if a record predates the migration.
      first_name: contact.first_name || (contact.name || '').split(' ')[0] || '',
      surname: contact.surname || ((contact.name || '').split(' ').slice(1).join(' ') || ''),
      address: contact.address || '',
      postcode: contact.postcode || '',
      telephone: contact.telephone || '',
      email: contact.email || '',
      company: contact.company || '',
      category: contact.category || 'Contractor',
      trade: contact.trade || '',
      marketing_consent: contact.marketing_consent || 'Awaiting',
      notes: contact.notes || '',
      show_in_diary: !!contact.show_in_diary,
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!formData.first_name.trim()) {
      toast.error('Please enter a first name (or company name)');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.put(`/contacts/${selectedContact.id}`, formData);
      toast.success('Contact updated successfully');
      setEditDialogOpen(false);
      setSelectedContact(null);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (contact) => {
    setSelectedContact(contact);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/contacts/${selectedContact.id}`);
      toast.success('Contact deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedContact(null);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    } finally {
      setSubmitting(false);
    }
  };

  // Dashboard stats
  const stats = {
    total: contacts.length,
    clients: contacts.filter(c => c.category === 'Client').length,
    architects: contacts.filter(c => c.category === 'Architect').length,
    contractors: contacts.filter(c => c.category === 'Contractor').length,
    marketingReady: contacts.filter(c => c.marketing_consent === 'Yes').length
  };

  // Pagination calculations
  const totalPages = Math.ceil(contacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedContacts = contacts.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, categoryFilter, consentFilter]);

  // CSV Export function
  const handleExportCSV = () => {
    if (contacts.length === 0) {
      toast.error('No contacts to export');
      return;
    }

    const headers = ['Name', 'Company', 'Category', 'Trade', 'Email', 'Telephone', 'Address', 'Postcode', 'Marketing Consent', 'Notes'];
    const csvRows = [headers.join(',')];

    contacts.forEach(contact => {
      const row = [
        `"${(contact.name || '').replace(/"/g, '""')}"`,
        `"${(contact.company || '').replace(/"/g, '""')}"`,
        `"${contact.category || ''}"`,
        `"${contact.trade || ''}"`,
        `"${contact.email || ''}"`,
        `"${contact.telephone || ''}"`,
        `"${(contact.address || '').replace(/"/g, '""')}"`,
        `"${contact.postcode || ''}"`,
        `"${contact.marketing_consent || ''}"`,
        `"${(contact.notes || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with filter info
    const filterParts = [];
    if (categoryFilter !== 'All') filterParts.push(categoryFilter);
    if (consentFilter !== 'All') filterParts.push(`consent-${consentFilter}`);
    if (search) filterParts.push('filtered');
    const filterSuffix = filterParts.length > 0 ? `_${filterParts.join('_')}` : '';
    
    link.download = `contacts${filterSuffix}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success(`Exported ${contacts.length} contacts to CSV`);
  };

  // Contacts are visible to everyone but edit/delete only for admins
  // (Previously admin-only, now surveyors can create but not edit/delete)

  // Contact form fields JSX - directly inlined to avoid re-render focus issues
  const contactFormFields = (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* SD-2 §3 — name split. Applied to every category so a first-name
            greeting is available in any future template (RFI to client,
            architect enquiries, etc). Business-style contacts can put the
            trading name into First Name and leave Surname blank. */}
        <div>
          <Label className="text-gray-300">First Name *</Label>
          <Input
            value={formData.first_name}
            onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
            placeholder="First name or company"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-first-name-input"
          />
        </div>
        <div>
          <Label className="text-gray-300">Surname</Label>
          <Input
            value={formData.surname}
            onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
            placeholder="Optional"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-surname-input"
          />
        </div>
        
        <div>
          <Label className="text-gray-300">Category *</Label>
          <Select
            value={formData.category}
            onValueChange={(v) => setFormData(prev => ({ ...prev, category: v, trade: v !== 'Supplier' ? '' : prev.trade }))}
          >
            <SelectTrigger className="bg-[#1E2530] border-white/20 text-white mt-1" data-testid="contact-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/20">
              {CATEGORY_OPTIONS.map(cat => (
                <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10">
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label className="text-gray-300">Company</Label>
          <Input
            value={formData.company}
            onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
            placeholder="Company name"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-company-input"
          />
        </div>
        
        {/* Trade field - only shown for Suppliers */}
        {formData.category === 'Supplier' && (
          <div>
            <Label className="text-gray-300">Trade *</Label>
            <Select
              value={formData.trade}
              onValueChange={(v) => setFormData(prev => ({ ...prev, trade: v }))}
            >
              <SelectTrigger className="bg-[#1E2530] border-white/20 text-white mt-1" data-testid="contact-trade-select">
                <SelectValue placeholder="Select trade" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/20">
                {SUPPLIER_TRADES.map(trade => (
                  <SelectItem key={trade} value={trade} className="text-white hover:bg-white/10">
                    {trade}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Show in Site Diary — only meaningful for Suppliers. Curates the
            Materials picker so surveyors don't see the whole CRM. */}
        {formData.category === 'Supplier' && (
          <div className="col-span-2">
            <label
              className="flex items-start gap-2.5 cursor-pointer select-none rounded-md px-3 py-2.5 bg-[#1E2530] border border-white/20 hover:bg-[#242C3A] transition-colors"
              data-testid="contact-show-in-diary-label"
            >
              <input
                type="checkbox"
                checked={!!formData.show_in_diary}
                onChange={(e) => setFormData(prev => ({ ...prev, show_in_diary: e.target.checked }))}
                className="mt-0.5 accent-[#F5A623] w-4 h-4"
                data-testid="contact-show-in-diary-checkbox"
              />
              <span>
                <span className="block text-[13px] font-medium text-white">Show in Site Diary</span>
                <span className="block text-[11px] text-gray-400">
                  Available in the Materials Order/Enquiry picker for surveyors.
                </span>
              </span>
            </label>
          </div>
        )}
        
        <div>
          <Label className="text-gray-300">Telephone</Label>
          <Input
            value={formData.telephone}
            onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
            placeholder="Phone number"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-telephone-input"
          />
        </div>
        
        <div>
          <Label className="text-gray-300">Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="email@example.com"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-email-input"
          />
        </div>
        
        <div className="col-span-2">
          <Label className="text-gray-300">Address</Label>
          <Input
            value={formData.address}
            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
            placeholder="Street address"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-address-input"
          />
        </div>
        
        <div>
          <Label className="text-gray-300">Postcode</Label>
          <Input
            value={formData.postcode}
            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
            placeholder="NN1 1AA"
            className="bg-[#1E2530] border-white/20 text-white mt-1"
            data-testid="contact-postcode-input"
          />
        </div>
        
        <div>
          <Label className="text-gray-300">Marketing Consent</Label>
          <Select
            value={formData.marketing_consent}
            onValueChange={(v) => setFormData(prev => ({ ...prev, marketing_consent: v }))}
          >
            <SelectTrigger className="bg-[#1E2530] border-white/20 text-white mt-1" data-testid="contact-consent-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#2A3240] border-white/20">
              {CONSENT_OPTIONS.map(opt => (
                <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="col-span-2">
          <Label className="text-gray-300">Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Add notes about consent, source, etc."
            className="bg-[#1E2530] border-white/20 text-white mt-1 min-h-[80px]"
            data-testid="contact-notes-input"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 bg-[#1E2530] min-h-screen">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Contacts
          </h1>
          <p className="text-gray-400 text-sm">{stats.total} contacts found</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={contacts.length === 0}
            className="border-white/20 text-gray-300 hover:bg-white/10"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setCreateDialogOpen(true);
            }}
            className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            data-testid="new-contact-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Contact
          </Button>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Total</p>
                <p className="text-xl font-bold text-white">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Clients</p>
                <p className="text-xl font-bold text-blue-400">{stats.clients}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Building2 className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Architects</p>
                <p className="text-xl font-bold text-purple-400">{stats.architects}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <HardHat className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Contractors</p>
                <p className="text-xl font-bold text-amber-400">{stats.contractors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-[#2A3240] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Marketing Ready</p>
                <p className="text-xl font-bold text-green-400">{stats.marketingReady}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="bg-[#2A3240] border-white/10 mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search by name, email, company, postcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-[#1E2530] border-white/20 text-white"
                data-testid="contacts-search-input"
              />
            </div>
            
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-full md:w-40 bg-[#1E2530] border-white/20 text-white" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/20">
                <SelectItem value="All" className="text-white hover:bg-white/10">All Categories</SelectItem>
                {CATEGORY_OPTIONS.map(cat => (
                  <SelectItem key={cat} value={cat} className="text-white hover:bg-white/10">
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={consentFilter} onValueChange={setConsentFilter}>
              <SelectTrigger className="w-full md:w-40 bg-[#1E2530] border-white/20 text-white" data-testid="consent-filter">
                <Filter className="w-4 h-4 mr-2 text-gray-500" />
                <SelectValue placeholder="Consent" />
              </SelectTrigger>
              <SelectContent className="bg-[#2A3240] border-white/20">
                <SelectItem value="All" className="text-white hover:bg-white/10">All Consent</SelectItem>
                {CONSENT_OPTIONS.map(opt => (
                  <SelectItem key={opt} value={opt} className="text-white hover:bg-white/10">
                    {opt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contacts Table */}
      <Card className="bg-[#2A3240] border-white/10">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-gray-400 font-medium">Name</TableHead>
              <TableHead className="text-gray-400 font-medium hidden md:table-cell">Company</TableHead>
              <TableHead className="text-gray-400 font-medium">Category</TableHead>
              <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Contact</TableHead>
              <TableHead className="text-gray-400 font-medium">Consent</TableHead>
              <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Tenders</TableHead>
              <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Success %</TableHead>
              <TableHead className="text-gray-400 font-medium w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-[#F5A623] mx-auto" />
                </TableCell>
              </TableRow>
            ) : contacts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                  No contacts found
                </TableCell>
              </TableRow>
            ) : (
              paginatedContacts.map((contact) => {
                const CategoryIcon = CATEGORY_ICONS[contact.category] || User;
                return (
                  <TableRow 
                    key={contact.id} 
                    className="border-white/10 hover:bg-white/5 cursor-pointer"
                    data-testid={`contact-row-${contact.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${CATEGORY_COLORS[contact.category]?.replace('text-', 'bg-').replace('400', '500/20')}`}>
                          <CategoryIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{contact.name}</p>
                          {contact.postcode && (
                            <p className="text-xs text-gray-500">{contact.postcode}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-gray-300">{contact.company || '-'}</span>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${CATEGORY_COLORS[contact.category]} border`}>
                        {contact.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="space-y-1">
                        {contact.telephone && (
                          <div className="flex items-center text-xs text-gray-400">
                            <Phone className="w-3 h-3 mr-1" />
                            {contact.telephone}
                          </div>
                        )}
                        {contact.email && (
                          <div className="flex items-center text-xs text-gray-400">
                            <Mail className="w-3 h-3 mr-1" />
                            {contact.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${CONSENT_COLORS[contact.marketing_consent]} border`}>
                        {contact.marketing_consent === 'Yes' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                        {contact.marketing_consent === 'No' && <XCircle className="w-3 h-3 mr-1" />}
                        {contact.marketing_consent === 'Awaiting' && <Clock className="w-3 h-3 mr-1" />}
                        {contact.marketing_consent}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {contact.tender_count > 0 ? (
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#F5A623]" />
                          <span className="text-[#F5A623]">{contact.tender_count}</span>
                          {contact.won_count > 0 && (
                            <span className="text-green-400 text-xs">({contact.won_count} won)</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {(contact.category === 'Contractor' || contact.category === 'Architect') && contact.tender_count > 0 ? (
                        <div className={`font-medium ${
                          contact.success_rate >= 50 ? 'text-green-400' : 
                          contact.success_rate >= 25 ? 'text-amber-400' : 
                          'text-red-400'
                        }`}>
                          {contact.success_rate}%
                        </div>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(contact);
                          }}
                          className="h-8 w-8 p-0 hover:bg-white/10"
                          data-testid={`view-contact-${contact.id}`}
                          title="View related projects/tenders"
                        >
                          <Eye className="w-4 h-4 text-blue-400" />
                        </Button>
                        {canEditRecords && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(contact);
                            }}
                            className="h-8 w-8 p-0 hover:bg-white/10"
                            data-testid={`edit-contact-${contact.id}`}
                          >
                            <Edit className="w-4 h-4 text-gray-400" />
                          </Button>
                        )}
                        {canDeleteRecords && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(contact);
                            }}
                            className="h-8 w-8 p-0 hover:bg-red-500/20"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 mt-4">
          <span className="text-sm text-gray-400">
            Showing {startIndex + 1}-{Math.min(endIndex, contacts.length)} of {contacts.length}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="h-8 w-8 p-0 border-white/20 text-gray-300 hover:bg-white/10 disabled:opacity-50"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            {/* Page numbers */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                // Show first, last, current, and pages near current
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - currentPage) <= 1) return true;
                return false;
              })
              .map((page, idx, arr) => {
                // Add ellipsis
                const showEllipsisBefore = idx > 0 && page - arr[idx - 1] > 1;
                return (
                  <span key={page} className="flex items-center">
                    {showEllipsisBefore && <span className="px-1 text-gray-500">...</span>}
                    <Button
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className={`h-8 w-8 p-0 ${
                        currentPage === page 
                          ? 'bg-[#F5A623] text-[#1E2530]' 
                          : 'border-white/20 text-gray-300 hover:bg-white/10'
                      }`}
                      data-testid={`pagination-page-${page}`}
                    >
                      {page}
                    </Button>
                  </span>
                );
              })}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="h-8 w-8 p-0 border-white/20 text-gray-300 hover:bg-white/10 disabled:opacity-50"
              data-testid="pagination-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Create Contact Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent 
          className="bg-[#2A3240] border-white/10 text-white max-w-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-[#F5A623]" />
              New Contact
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a new client, architect, or contractor to your CRM.
            </DialogDescription>
          </DialogHeader>
          
          {contactFormFields}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={submitting}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="create-contact-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent 
          className="bg-[#2A3240] border-white/10 text-white max-w-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#F5A623]" />
              Edit Contact
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Update contact details and marketing consent.
            </DialogDescription>
          </DialogHeader>
          
          {contactFormFields}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={submitting}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
              data-testid="update-contact-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete Contact
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to delete <strong className="text-white">{selectedContact?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/20 text-gray-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-contact"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Contact Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-[#2A3240] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {selectedContact && CATEGORY_ICONS[selectedContact.category] && 
                (() => {
                  const Icon = CATEGORY_ICONS[selectedContact.category];
                  return <Icon className="w-5 h-5 text-[#F5A623]" />;
                })()
              }
              {selectedContact?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedContact?.category} • {selectedContact?.company || 'No company'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {selectedContact?.email && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${selectedContact.email}`} className="hover:text-[#F5A623]">{selectedContact.email}</a>
                </div>
              )}
              {selectedContact?.telephone && (
                <div className="flex items-center gap-2 text-gray-400">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${selectedContact.telephone}`} className="hover:text-[#F5A623]">{selectedContact.telephone}</a>
                </div>
              )}
              {selectedContact?.address && (
                <div className="flex items-center gap-2 text-gray-400 col-span-2">
                  <MapPin className="w-4 h-4" />
                  {selectedContact.address}{selectedContact.postcode ? `, ${selectedContact.postcode}` : ''}
                </div>
              )}
            </div>

            {loadingRelated ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#F5A623]" />
              </div>
            ) : relatedItems && (
              <>
                {/* Related Tenders */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-400" />
                    Related Tenders
                    <Badge className="bg-purple-500/20 text-purple-400 text-xs">
                      {relatedItems.summary.total_tenders}
                    </Badge>
                  </h3>
                  
                  {relatedItems.tenders.as_client.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">As Client:</p>
                      <div className="space-y-1">
                        {relatedItems.tenders.as_client.map(t => (
                          <Link 
                            key={t.id} 
                            to={`/tenders/${t.id}`}
                            className="flex items-center justify-between p-2 bg-[#1E2530] rounded hover:bg-[#252D3B] transition-colors"
                            onClick={() => setViewDialogOpen(false)}
                          >
                            <span className="text-sm text-white">{t.ref}</span>
                            <Badge className="text-xs bg-gray-500/20 text-gray-400">{t.status}</Badge>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {relatedItems.tenders.as_architect.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">As Architect:</p>
                      <div className="space-y-1">
                        {relatedItems.tenders.as_architect.map(t => (
                          <Link 
                            key={t.id} 
                            to={`/tenders/${t.id}`}
                            className="flex items-center justify-between p-2 bg-[#1E2530] rounded hover:bg-[#252D3B] transition-colors"
                            onClick={() => setViewDialogOpen(false)}
                          >
                            <span className="text-sm text-white">{t.ref}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{t.client}</span>
                              <Badge className="text-xs bg-gray-500/20 text-gray-400">{t.status}</Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {relatedItems.summary.total_tenders === 0 && (
                    <p className="text-gray-500 text-sm">No related tenders</p>
                  )}
                </div>

                {/* Related Projects */}
                <div>
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-green-400" />
                    Related Projects
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                      {relatedItems.summary.total_projects}
                    </Badge>
                  </h3>
                  
                  {relatedItems.projects.as_client.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">As Client:</p>
                      <div className="space-y-1">
                        {relatedItems.projects.as_client.map(p => (
                          <Link 
                            key={p.id} 
                            to={`/projects/${p.id}`}
                            className="flex items-center justify-between p-2 bg-[#1E2530] rounded hover:bg-[#252D3B] transition-colors"
                            onClick={() => setViewDialogOpen(false)}
                          >
                            <span className="text-sm text-white">{p.ref}</span>
                            <Badge className="text-xs bg-gray-500/20 text-gray-400">{p.status}</Badge>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {relatedItems.projects.as_architect.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">As Architect:</p>
                      <div className="space-y-1">
                        {relatedItems.projects.as_architect.map(p => (
                          <Link 
                            key={p.id} 
                            to={`/projects/${p.id}`}
                            className="flex items-center justify-between p-2 bg-[#1E2530] rounded hover:bg-[#252D3B] transition-colors"
                            onClick={() => setViewDialogOpen(false)}
                          >
                            <span className="text-sm text-white">{p.ref}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{p.client}</span>
                              <Badge className="text-xs bg-gray-500/20 text-gray-400">{p.status}</Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {relatedItems.projects.as_contractor.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-2">As Contractor:</p>
                      <div className="space-y-1">
                        {relatedItems.projects.as_contractor.map(p => (
                          <Link 
                            key={p.id} 
                            to={`/projects/${p.id}`}
                            className="flex items-center justify-between p-2 bg-[#1E2530] rounded hover:bg-[#252D3B] transition-colors"
                            onClick={() => setViewDialogOpen(false)}
                          >
                            <span className="text-sm text-white">{p.ref}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{p.client}</span>
                              <Badge className="text-xs bg-gray-500/20 text-gray-400">{p.status}</Badge>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {relatedItems.summary.total_projects === 0 && (
                    <p className="text-gray-500 text-sm">No related projects</p>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setViewDialogOpen(false)}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
